import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';

import type { ReviewWizardApi } from '../../features/audit-whiz/hooks/useReviewWizard';

/** The footer-button label keys this module resolves (a subset of the page's
 *  i18n keys). */
export type FooterKey =
  | 'skipForNowButton'
  | 'backButton'
  | 'nextButton'
  | 'publishButton';

/** One footer action button's config. Rendered by {@link ActionButton} so the
 *  page's buttons share a single JSX site instead of duplicating markup. */
export interface ActionConfig {
  readonly testId: string;
  readonly label: string;
  readonly variant: ButtonVariant;
  readonly onClick: () => void;
  readonly disabled?: boolean;
}

/** Renders one footer action from its config. */
export function ActionButton({ action }: { readonly action: ActionConfig }): JSX.Element {
  return (
    <Button
      variant={action.variant}
      disabled={action.disabled ?? false}
      onClick={action.onClick}
      data-testid={action.testId}
    >
      {action.label}
    </Button>
  );
}

/**
 * The footer buttons for the current step (matches the mock): Skip-for-now
 * always sits on the left; the right-hand set is step-dependent —
 *   Audit Criteria: [Next]
 *   Sub Questions:  [Previous] [Next]
 *   Configuration:  [Previous] [Save & publish]
 * Save & publish is disabled once published, while ANY step has validation
 * errors (`!canPublish`), or while a publish request is already in flight
 * (`isPublishing`); `beginPublish`'s null gate is the backstop. The async
 * publish orchestration lives in the page and is passed in as `onPublish`.
 * Extracted from the page body to keep the component within the line budget.
 */
export function buildFooterActions(
  wizard: ReviewWizardApi,
  t: (key: FooterKey) => string,
  isPublished: boolean,
  testIdRoot: string,
  onPublish: () => void,
  isPublishing: boolean,
): { skipAction: ActionConfig; stepActions: readonly ActionConfig[] } {
  const skipAction: ActionConfig = {
    testId: `${testIdRoot}-skip`,
    label: t('skipForNowButton'),
    variant: ButtonVariant.Tertiary,
    onClick: wizard.skipForNow,
  };
  const previousAction: ActionConfig = {
    testId: `${testIdRoot}-back`,
    label: t('backButton'),
    variant: ButtonVariant.Secondary,
    onClick: wizard.goPrev,
  };
  const nextAction: ActionConfig = {
    testId: `${testIdRoot}-next`,
    label: t('nextButton'),
    variant: ButtonVariant.Primary,
    onClick: wizard.goNext,
  };
  const publishAction: ActionConfig = {
    testId: `${testIdRoot}-publish`,
    label: t('publishButton'),
    variant: ButtonVariant.Primary,
    onClick: onPublish,
    disabled: isPublished || !wizard.canPublish || isPublishing,
  };
  const stepActions: readonly ActionConfig[] = wizard.isLastStep
    ? [previousAction, publishAction]
    : [...(wizard.isFirstStep ? [] : [previousAction]), nextAction];
  return { skipAction, stepActions };
}
